import {
  BioSampleContainerCriteria,
  BioSampleContainerQuery,
  BioSampleContainerSort,
  BioSampleKitType,
} from '@csp/csp-common-hbs-model';
import {
  BioSamplingContainerCriteriaFieldsV1,
  BioSamplingContainerDateRangeFieldsV1,
  BioSamplingContainerQueryCriterionV1,
  BioSamplingContainerQueryDateRangeCriterionV1,
  BioSamplingContainerQueryOperatorV1,
  BioSamplingContainerQuerySortOrderV1,
  BioSamplingContainerQueryTypeV1,
  BioSamplingContainerQueryV1,
  BioSamplingContainerSortFieldsV1,
  BioSamplingSupplyKitInfoQueryInV1,
} from '@csp/dmdp-api-hbs-supply-dto';
import { Maybe } from '@csp/csp-common-model';
import { isDefined } from '@csp/csp-common-util';
import { BioSampleMapper } from './BioSampleMapper';

const appendNewerThan = (
  criteria: Maybe<BioSampleContainerCriteria>,
  criteriaV1: BioSamplingContainerQueryCriterionV1[],
): void => {
  if (criteria?.newerThan) {
    criteriaV1.push({
      fieldCriterion: {
        field: BioSamplingContainerCriteriaFieldsV1.EVENT_ID,
        type: BioSamplingContainerQueryTypeV1.GREATER_THAN,
        values: [criteria.newerThan],
      },
    });
  }
};

const appendKitTypes = (
  criteria: Maybe<BioSampleContainerCriteria>,
  criteriaV1: BioSamplingContainerQueryCriterionV1[],
): void => {
  if (criteria?.kitTypes?.length) {
    criteriaV1.push({
      fieldCriterion: {
        field: BioSamplingContainerCriteriaFieldsV1.KIT_TYPE,
        type: BioSamplingContainerQueryTypeV1.IN,
        values: criteria.kitTypes,
      },
    });
  }
};

const appendSiteNumbers = (
  criteria: Maybe<BioSampleContainerCriteria>,
  criteriaV1: BioSamplingContainerQueryCriterionV1[],
): void => {
  if (criteria?.siteNumbers?.length) {
    criteriaV1.push({
      fieldCriterion: {
        field: BioSamplingContainerCriteriaFieldsV1.PRACTICE_CODE,
        type: BioSamplingContainerQueryTypeV1.IN,
        values: criteria.siteNumbers,
      },
    });
  }
};

const toSampleContainerRefsCriteria = (
  criteria: Maybe<BioSampleContainerCriteria>,
): BioSamplingContainerQueryCriterionV1[] =>
  criteria?.sampleContainerRefs
    ?.filter(ref => !!ref.kitCode || !!ref.containerNumber)
    .flatMap(ref => {
      const refCriteriaV1: BioSamplingContainerQueryCriterionV1[] = [];
      if (ref.kitCode) {
        refCriteriaV1.push({
          fieldCriterion: {
            field: BioSamplingContainerCriteriaFieldsV1.KIT_CODE,
            type: BioSamplingContainerQueryTypeV1.EQ,
            values: [ref.kitCode],
          },
        });
      }
      if (ref.containerNumber) {
        refCriteriaV1.push({
          fieldCriterion: {
            field: BioSamplingContainerCriteriaFieldsV1.CONTAINER_NUMBER,
            type: BioSamplingContainerQueryTypeV1.EQ,
            values: [
              // Remove potential zero-padding
              ref.format
                ? BioSampleMapper.toBioSampleContainerNumber(ref.containerNumber, ref.format).containerNumber
                : ref.containerNumber,
            ],
          },
        });
      }
      return {
        operator: BioSamplingContainerQueryOperatorV1.AND,
        criteria: refCriteriaV1,
      };
    }) ?? [];

const appendSampleContainerRefsAndSampleContainerCodes = (
  criteria: Maybe<BioSampleContainerCriteria>,
  criteriaV1: BioSamplingContainerQueryCriterionV1[],
): void => {
  const containerRefsCriterionV1s: BioSamplingContainerQueryCriterionV1[] = toSampleContainerRefsCriteria(criteria);

  const sampleContainerCodeCriterionV1: Maybe<BioSamplingContainerQueryCriterionV1> = criteria?.sampleContainerCodes
    ?.length
    ? {
        fieldCriterion: {
          field: BioSamplingContainerCriteriaFieldsV1.CONTAINER_CODE,
          type: BioSamplingContainerQueryTypeV1.IN,
          values: criteria.sampleContainerCodes,
        },
      }
    : undefined;

  const criterionV1s = [...containerRefsCriterionV1s, sampleContainerCodeCriterionV1].filter(isDefined);

  if (criterionV1s.length) {
    criteriaV1.push({
      operator: BioSamplingContainerQueryOperatorV1.OR,
      criteria: criterionV1s,
    });
  }
};

const appendLikeKitOrSampleContainerCode = (
  criteria: Maybe<BioSampleContainerCriteria>,
  criteriaV1: BioSamplingContainerQueryCriterionV1[],
): void => {
  const text = criteria?.likeKitOrSampleContainerCode?.trim();
  if (text?.length) {
    criteriaV1.push({
      operator: BioSamplingContainerQueryOperatorV1.OR,
      criteria: [
        {
          fieldCriterion: {
            field: BioSamplingContainerCriteriaFieldsV1.KIT_CODE,
            type: BioSamplingContainerQueryTypeV1.LIKE,
            values: [text],
          },
        },
        {
          fieldCriterion: {
            field: BioSamplingContainerCriteriaFieldsV1.CONTAINER_NUMBER,
            type: BioSamplingContainerQueryTypeV1.LIKE,
            values: [text],
          },
        },
      ],
    });
  }
};

const toDateRangeCriteriaV1 = ({
  createdIsoDateFromIncl,
  createdIsoDateToIncl,
}: Maybe<BioSampleContainerCriteria> = {}): Maybe<BioSamplingContainerQueryDateRangeCriterionV1[]> => {
  const v1s: BioSamplingContainerQueryDateRangeCriterionV1[] = [];
  if (createdIsoDateFromIncl || createdIsoDateToIncl) {
    v1s.push({
      field: BioSamplingContainerDateRangeFieldsV1.CREATED_AT,
      fromInclusive: true,
      from: createdIsoDateFromIncl,
      toInclusive: true,
      to: createdIsoDateToIncl,
    });
  }
  return v1s.length ? v1s : undefined;
};

const toQueryV1 = ({
  criteria,
  fields,
  sort,
}: Omit<BioSampleContainerQuery, 'pageSize' | 'count'>): BioSamplingContainerQueryV1 => {
  const criteriaV1: BioSamplingContainerQueryCriterionV1[] = [];

  appendNewerThan(criteria, criteriaV1);
  appendKitTypes(criteria, criteriaV1);
  appendSiteNumbers(criteria, criteriaV1);
  appendSampleContainerRefsAndSampleContainerCodes(criteria, criteriaV1);
  appendLikeKitOrSampleContainerCode(criteria, criteriaV1);

  const criterion: Maybe<BioSamplingContainerQueryCriterionV1> = criteriaV1.length
    ? {
        criteria: criteriaV1,
        operator: BioSamplingContainerQueryOperatorV1.AND,
      }
    : undefined;

  return {
    dateRangeCriteria: toDateRangeCriteriaV1(criteria),
    sort,
    fields,
    criterion,
  };
};

const DEFAULT_SORTING: BioSampleContainerSort[] = [
  {
    field: BioSamplingContainerSortFieldsV1.KIT_CODE,
    order: BioSamplingContainerQuerySortOrderV1.DESC,
  },
  {
    field: BioSamplingContainerSortFieldsV1.CONTAINER_NUMBER,
    order: BioSamplingContainerQuerySortOrderV1.ASC,
  },
];

const mergeQuery = (partial: Partial<BioSampleContainerQuery>): BioSampleContainerQuery => ({
  pageSize: 5_000,
  count: false,
  sort: DEFAULT_SORTING,
  ...partial,
});

const toQuery = (criteria: BioSampleContainerCriteria, pageSize = 5_000): BioSampleContainerQuery =>
  mergeQuery({
    pageSize,
    criteria,
  });

const toKitInfoQueryV1 = (kitTypes: BioSampleKitType[]): BioSamplingSupplyKitInfoQueryInV1 => ({
  kitTypes: kitTypes.map(kitType => ({
    kitType,
  })),
});

export const BioSampleContainerQueryMapper = {
  mergeQuery,
  toQuery,
  toQueryV1,
  toKitInfoQueryV1,
  defaultSorting: DEFAULT_SORTING,
};
